/** Hub 的“控制面 / 观测面”双通道模型。
 *
 * 控制面（浏览器 -> Worker -> Hub）是唯一可写入口，也只在打开工作区时探测一次
 * `/state`（判断可写 + 更新可达租约），不做周期轮询。
 * 观测面（浏览器 -> Hub /_live/）是运维实时数据的唯一来源；连不上时显示
 * “实时观测未连接”，不回退控制面快照。
 */
import { useQuery } from '@tanstack/react-query';
import { hubStateQueryOptions, useMarkHubReachable } from './queries';
import { useLiveHub, type LiveObservation, type LiveStatus } from './live';

export type HubPresence = 'managed-online' | 'live-only' | 'offline' | 'unknown';
/** 运维观测数据来源：只有浏览器直连 live，或没有。 */
export type ObservationSource = 'live' | 'none';

export interface HubChannels {
  control: {
    reachable: boolean | null;
    error: string | null;
  };
  live: {
    status: LiveStatus;
    lastFrameAt: number | null;
    error: string | null;
  };
  presence: HubPresence;
  /** 保存期望配置（PUT apps 配置 / 节点字段，纯 D1 写）永远允许，与通道无关。 */
  canSave: boolean;
  /** 执行类动作（plan/apply/rollback/自定义动作）需要 Worker→Hub 通道。 */
  canApply: boolean;
  /** 兼容别名（= canApply）：执行类写闸门。存量页面逐步迁移到 canApply。 */
  canMutate: boolean;
  readSource: ObservationSource;
}

export function deriveHubChannels(input: {
  controlReachable: boolean | null;
  controlError?: string | null;
  live: Pick<LiveObservation, 'status' | 'snapshot' | 'error'>;
  knownStatus?: 'online' | 'offline' | 'unknown' | null;
}): HubChannels {
  const { controlReachable, controlError = null, live, knownStatus = null } = input;
  const liveLive = live.status === 'live' && live.snapshot !== null;
  const presence: HubPresence =
    controlReachable === true
      ? 'managed-online'
      : liveLive
        ? 'live-only'
        : controlReachable === false || knownStatus === 'offline'
          ? 'offline'
          : 'unknown';
  return {
    control: { reachable: controlReachable, error: controlError },
    live: {
      status: live.status,
      lastFrameAt: live.snapshot?.receivedAt ?? null,
      error: live.error,
    },
    presence,
    canSave: true,
    canApply: controlReachable === true,
    canMutate: controlReachable === true,
    readSource: liveLive ? 'live' : 'none',
  };
}

export function useHubChannels(
  hubId: string,
  liveEndpoint: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  // 只在打开工作区时探测一次；手动重试由页面/提示条触发，不做周期轮询。
  const stateQuery = useQuery({
    ...hubStateQueryOptions(hubId),
    enabled: options.enabled ?? true,
  });
  const live = useLiveHub(hubId, liveEndpoint);
  useMarkHubReachable(hubId, stateQuery.data?.reachable);
  const channels = deriveHubChannels({
    controlReachable: stateQuery.data?.reachable ?? null,
    controlError: stateQuery.data?.error ?? null,
    live,
  });
  return { stateQuery, live, channels };
}
